// kfe-squiggle.js — squiggle pathway renderer (single page).
//
// PORT TARGET: Assets/KFE/Scripts/Runtime/Core/UI/Pathway/SquiggleRenderer.cs
//
// Renders one PathwayPage (see src/data/pathway-builder.js) into an SVG
// squiggle: zigzag rows of numbered circles connected by dotted lines, with
// a half-circle U-curve at row transitions, an optional dashed next-page
// arrow rising off the topmost node, and a star milestone treatment for
// any node flagged as `isStarMilestone`.
//
// Brand canon (per the 2016 GUI design guide and current iOS app):
//   - Dark theme. Nodes are dark fill + white border + white number.
//   - Node-number colour is keyed off completion state, mirroring iOS
//     `LessonsViewController.m` (`redrawSquiggles` + then
//     `updateSquigglesCompletedItemsToGreen`) and the colour constants in
//     `KFEGatingLineView.m`:
//       · COMPLETED  → lime-green `#8CC63F`  (kPassedTextColor / StatePassed)
//       · accessible → white      `#FFFFFF`  (kPlayingTextColor / NormalPlaying)
//       · locked     → dimmed grey           (kUnavailableTextColor #6D6D6D;
//                                              web dims the whole node via
//                                              --kfe-locked-opacity)
//     The green is EXCLUSIVELY the completed cue — iOS sets every `completed==1`
//     node to Passed (green) LAST, overriding the redraw; the current/next
//     accessible node stays white, not green. (Brief 153 corrected the prior web
//     mapping, which lit the *available* node green — a divergence from canon
//     that turned every accessible lesson green.) iOS applies the green with no
//     gating check, so it's a learner-progress signal independent of gating mode.
//   - Path stroke colour comes from the `level` prop: orange for stage
//     pathway, cyan for lesson pathway. Same component, level-parameterised.
//   - Bottom-up orientation: lower-numbered nodes sit at the bottom of the
//     SVG, higher-numbered at the top. Climbing metaphor for language
//     learning. Next-page arrow exits off the top, not the bottom.
//
// API
// ───
//   <kfe-squiggle .page=${pathwayPage}
//                 .level=${"stage" | "lesson"}
//                 .gatingEnabled=${true | false}
//                 on:select=${(e) => navigate(`/lesson/${e.detail.itemId}`)}
//                 on:locked=${(e) => toast(e.detail.message)}>
//   </kfe-squiggle>
//
// Tap on an Available or Completed node fires `select` with detail
// `{ itemId, displayNumber }`. Tap on a Locked node fires a `locked`
// event with detail `{ itemId, displayNumber, message }` AND surfaces a
// short live-region message inside the component itself so it works even
// if the parent doesn't subscribe (per brief 011 success criterion #2).
// When gating is off, every node is tappable and `locked` never fires.

import { KFEComponent } from "../core/component.js";
import { t } from "../core/i18n.js";

const SVG_NS = "http://www.w3.org/2000/svg";

// Layout constants are in viewBox units (≈ CSS pixels at the project's
// typical pathway width). The host CSS clamps the rendered width so a node
// never shrinks below ~44 CSS-px touch-target diameter.
const W           = 360;
const NODE_RADIUS = 22;
const HIT_RADIUS  = 32;
// Side pad has to leave room for the U-curve bulge (radius = ROW_HEIGHT/2)
// to render inside the viewBox. SIDE_PAD ≥ ROW_HEIGHT/2 keeps the arc on
// canvas without relying on overflow:visible.
const SIDE_PAD    = 90;
const ROW_HEIGHT  = 170;
const TOP_PAD     = 56;
const BOTTOM_PAD  = 56;
// Star sized to fit comfortably inside the white-bordered circle.
const STAR_OUTER  = 16;
const STAR_INNER  = 7;

/** 10-point star polygon, centred on (0,0). */
const STAR_POINTS = (() => {
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const angle = -Math.PI / 2 + (Math.PI / 5) * i;
    const r = i % 2 === 0 ? STAR_OUTER : STAR_INNER;
    pts.push((Math.cos(angle) * r).toFixed(2) + "," + (Math.sin(angle) * r).toFixed(2));
  }
  return pts.join(" ");
})();

export class KFESquiggle extends KFEComponent {
  static props = ["page", "level", "gatingEnabled"];
  static styles = `
    :host {
      display: block;
      width: 100%;
      min-width: 280px;
      max-width: 480px;
      margin-inline: auto;
    }
    svg { display: block; width: 100%; height: auto; overflow: visible; }

    /* Path stroke colour is set on the host via CSS custom property based
       on level (stage = orange, lesson = cyan). */
    .line, .arrow {
      fill: none;
      stroke: var(--kfe-line-stroke, var(--kfe-line-stage));
      stroke-width: 8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .arrow { stroke-dasharray: 9 9; }
    .arrowhead { fill: var(--kfe-line-stroke, var(--kfe-line-stage)); stroke: none; }

    /* Locked treatment.
       The path stroke stays at full brightness regardless — iOS canon
       (e.g. IMG_2361 stage pathway) shows the full path bright orange
       even when only stage 1 is the active/current node. Only the
       locked node's ring + number + star dim. The dark fill stays
       fully opaque so the line passing behind a locked node is
       visually terminated at the ring perimeter. */
    .node.locked .ring,
    .node.locked text,
    .node.locked .star { opacity: var(--kfe-locked-opacity); }

    .node .fill { fill: var(--kfe-node-fill); }
    .node .ring { fill: none; stroke: var(--kfe-node-border); stroke-width: 3; }
    .node text {
      font-family: var(--kfe-font);
      font-weight: 700;
      font-size: 18px;
      text-anchor: middle;
      dominant-baseline: central;
      fill: var(--kfe-node-text);
      pointer-events: none;
      user-select: none;
    }
    /* Lime number for a COMPLETED node — iOS kPassedTextColor (#8CC63F),
       applied last so it overrides the default white. The current/next
       accessible node stays white (iOS NormalPlaying); the only other cue is
       locked dimming. (Brief 153 — was previously keyed off the available node.) */
    .node.completed text { fill: var(--kfe-node-completed); }

    .node .star {
      fill: var(--kfe-star-fill);
      stroke: var(--kfe-star-stroke);
      stroke-width: 1.5;
    }

    .node.tappable { cursor: pointer; }
    .hit { fill: transparent; }
    .node:focus-visible .ring { stroke-width: 5; outline: none; }

    /* Locked-tap toast — short, transient, screen-reader-friendly via
       aria-live="polite". Sits over the squiggle so it doesn't reflow the
       page. CSS-only fade so we don't ship a JS animation runner for it. */
    .locked-toast {
      position: absolute;
      left: 50%;
      bottom: 0.75rem;
      transform: translateX(-50%);
      padding: 0.5rem 0.9rem;
      border-radius: 999px;
      background: var(--kfe-toast-bg, rgba(20, 20, 20, 0.92));
      color: var(--kfe-toast-fg, #fff);
      font-size: 0.85rem;
      font-family: var(--kfe-font);
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.18s ease-in-out;
      max-width: 80%;
      text-align: center;
      white-space: nowrap;
    }
    .locked-toast.shown { opacity: 1; }
    :host { position: relative; }
  `;

  /** @type {ReturnType<typeof setTimeout> | null} */
  _toastTimer = null;

  /** @returns {DocumentFragment | undefined} */
  render() {
    const page = /** @type {import("../data/pathway-builder.js").PathwayPage | null | undefined} */ (
      /** @type {any} */ (this).page
    );
    if (!page || page.nodes.length === 0) return undefined;

    const level = /** @type {any} */ (this).level === "lesson" ? "lesson" : "stage";
    const gatingEnabled = /** @type {any} */ (this).gatingEnabled !== false;

    const stroke = level === "lesson" ? "var(--kfe-line-lesson)" : "var(--kfe-line-stage)";
    this.style.setProperty("--kfe-line-stroke", stroke);

    const layout = computeLayout(page);
    const { positions, transitions, height } = layout;
    const states = page.nodes;

    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("viewBox", `0 0 ${W} ${height}`);
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    svg.setAttribute("role", "list");
    svg.setAttribute("aria-label", level === "lesson" ? "Lesson pathway" : "Stage pathway");

    // Connectors first so nodes overlay them. Path stays at full brightness
    // regardless of locked state of the next node — only the node's ring +
    // number dim. Matches iOS canon (the path is always full-saturation).
    for (let i = 0; i < positions.length - 1; i++) {
      const from = positions[i];
      const to = positions[i + 1];
      const path = document.createElementNS(SVG_NS, "path");
      path.setAttribute(
        "d",
        transitions[i] ? uCurvePath(from, to) : `M ${from.x} ${from.y} L ${to.x} ${to.y}`,
      );
      path.setAttribute("class", "line");
      svg.appendChild(path);
    }

    if (page.hasNextPage) {
      // Bottom-up: the next page is "above". Arrow rises off the topmost
      // node, curving up-and-right into the next horizontal scroll-snap page.
      drawNextPageArrow(svg, positions[positions.length - 1]);
    }

    for (let i = 0; i < states.length; i++) {
      const state = states[i];
      const displayNumber = page.startNumber + i;
      const isLocked = gatingEnabled && state.nodeState === "locked";
      // iOS canon: green is the COMPLETED cue (kPassedTextColor), not the
      // current/available one. A completed node is never locked, so the
      // gating-off case still renders its green correctly.
      const isCompleted = state.nodeState === "completed";
      const node = makeNode(positions[i], state, displayNumber, isLocked, isCompleted);
      node.addEventListener("click", () => {
        if (isLocked) {
          const message = t("pathway.locked");
          this._showLockedToast(message);
          this.dispatchEvent(new CustomEvent("locked", {
            detail: { itemId: state.itemId, displayNumber, message },
            bubbles: true,
            composed: true,
          }));
          return;
        }
        this.dispatchEvent(new CustomEvent("select", {
          detail: { itemId: state.itemId, displayNumber },
          bubbles: true,
          composed: true,
        }));
      });
      svg.appendChild(node);
    }

    const toast = document.createElement("div");
    toast.className = "locked-toast";
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-live", "polite");
    toast.dataset.role = "locked-toast";

    const frag = document.createDocumentFragment();
    frag.appendChild(svg);
    frag.appendChild(toast);
    return frag;
  }

  /** @param {string} message */
  _showLockedToast(message) {
    const toast = /** @type {HTMLElement | null} */ (
      this.shadowRoot?.querySelector('[data-role="locked-toast"]')
    );
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add("shown");
    if (this._toastTimer != null) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      toast.classList.remove("shown");
      this._toastTimer = null;
    }, 1800);
  }

  onDisconnected() {
    if (this._toastTimer != null) {
      clearTimeout(this._toastTimer);
      this._toastTimer = null;
    }
  }
}

/**
 * @typedef {{ x: number, y: number }} Point
 *
 * @typedef {object} Layout
 * @property {Point[]}   positions
 * @property {boolean[]} transitions  transitions[i] === true if connector i→i+1 spans a row break (U-curve)
 * @property {number}    height       viewBox height
 */

/**
 * Compute a bottom-up row-grid layout. Row 0 (lowest-numbered) sits at the
 * bottom of the SVG, the snake zigzags upward, and row N (highest) is at
 * the top. Even rows read LTR, odd rows RTL; row-break transitions become
 * half-circle U-curves with radius = ROW_HEIGHT/2.
 *
 * @param {import("../data/pathway-builder.js").PathwayPage} page
 * @returns {Layout}
 */
function computeLayout(page) {
  const total = page.nodes.length;
  /** @type {Point[]} */ const positions = [];
  /** @type {boolean[]} */ const transitions = [];

  // Row-grid: rows of 3, or 4 if many nodes (mirrors Unity line 150).
  const perRow = total > 9 ? 4 : 3;
  const usable = W - SIDE_PAD * 2;
  const spacing = perRow > 1 ? usable / (perRow - 1) : 0;
  const rows = Math.ceil(total / perRow);
  // Row centres land at (TOP_PAD + ROW_HEIGHT/2 + rowFromTop*ROW_HEIGHT).
  // Total height = top pad + #rows × row height − ½ row + bottom pad.
  const height = TOP_PAD + rows * ROW_HEIGHT - ROW_HEIGHT / 2 + BOTTOM_PAD;

  for (let i = 0; i < total; i++) {
    const row = Math.floor(i / perRow);
    const col = i % perRow;
    const ltr = row % 2 === 0;
    const x = SIDE_PAD + (ltr ? col : perRow - 1 - col) * spacing;
    // Bottom-up: invert the row index so row 0 lands at the bottom.
    const rowFromTop = rows - 1 - row;
    const y = TOP_PAD + ROW_HEIGHT * 0.5 + rowFromTop * ROW_HEIGHT;
    positions.push({ x, y });
  }

  for (let i = 0; i < positions.length - 1; i++) {
    transitions[i] = Math.floor(i / perRow) !== Math.floor((i + 1) / perRow);
  }

  return { positions, transitions, height };
}

/**
 * Half-circle U-curve from `from` to `to`. Both endpoints sit on the same
 * x (the left or right edge of the row); the arc bulges outward by exactly
 * the radius (= rowGap/2) so it hits the next row tangentially.
 *
 * @param {Point} from
 * @param {Point} to
 */
function uCurvePath(from, to) {
  const r = Math.abs(from.y - to.y) / 2;
  // SVG arc sweep in y-down coords, going bottom→top at the same x:
  //   sweep=0 (counter-clockwise on screen) → arc bulges right
  //   sweep=1 (clockwise on screen)        → arc bulges left
  // Right-edge transitions need to bulge right (outward); left-edge bulge left.
  const sweep = from.x > W * 0.5 ? 0 : 1;
  return `M ${from.x} ${from.y} A ${r} ${r} 0 0 ${sweep} ${to.x} ${to.y}`;
}

/**
 * Append a dashed next-page arrow that rises off the topmost (highest-
 * numbered) node and curves up-and-right toward the next page in the
 * horizontal scroll-snap container. Mirrors the previous top-down arrow,
 * flipped to point upward for the bottom-up orientation.
 *
 * @param {SVGSVGElement} svg
 * @param {Point} top
 */
function drawNextPageArrow(svg, top) {
  // Negative dy = up in SVG coords.
  const dx = W * 0.22;
  const dy = -ROW_HEIGHT * 0.6;
  const p0 = top;
  const p1 = { x: top.x + dx * 0.4, y: top.y + dy * 0.3 };
  const p2 = { x: top.x + dx,       y: top.y + dy * 0.7 };
  const p3 = { x: top.x + dx * 0.6, y: top.y + dy };

  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute(
    "d",
    `M ${p0.x} ${p0.y} C ${p1.x} ${p1.y}, ${p2.x} ${p2.y}, ${p3.x} ${p3.y}`,
  );
  path.setAttribute("class", "arrow");
  svg.appendChild(path);

  // Arrowhead: small triangle aimed along the tangent at p3.
  const tx = p3.x - p2.x;
  const ty = p3.y - p2.y;
  const len = Math.sqrt(tx * tx + ty * ty) || 1;
  const ux = tx / len;
  const uy = ty / len;
  const px = -uy;
  const py = ux;
  const size = 9;
  const a = `${p3.x},${p3.y}`;
  const b = `${(p3.x - ux * size + px * size * 0.6).toFixed(2)},${(p3.y - uy * size + py * size * 0.6).toFixed(2)}`;
  const c = `${(p3.x - ux * size - px * size * 0.6).toFixed(2)},${(p3.y - uy * size - py * size * 0.6).toFixed(2)}`;
  const tri = document.createElementNS(SVG_NS, "polygon");
  tri.setAttribute("points", `${a} ${b} ${c}`);
  tri.setAttribute("class", "arrowhead");
  svg.appendChild(tri);
}

/**
 * Build a single node group at `pos`. Always renders the white-bordered
 * dark circle; on top of that, either the number (default) or a tan/gold
 * star (if `state.isStarMilestone`). A transparent hit-target circle gives
 * the touch area a comfortable ~32-px radius on phones.
 *
 * @param {Point} pos
 * @param {import("../data/pathway-builder.js").ItemState} state
 * @param {number}  displayNumber
 * @param {boolean} isLocked
 * @param {boolean} isCompleted
 */
function makeNode(pos, state, displayNumber, isLocked, isCompleted) {
  const g = document.createElementNS(SVG_NS, "g");
  const classes = ["node"];
  if (isLocked) classes.push("locked");
  else classes.push("tappable");
  if (isCompleted) classes.push("completed");
  g.setAttribute("class", classes.join(" "));
  g.setAttribute("role", "button");
  g.setAttribute("tabindex", isLocked ? "-1" : "0");
  const ariaLabel = state.title
    ? `${displayNumber}. ${state.title} (${state.nodeState})`
    : `Item ${displayNumber} (${state.nodeState})`;
  g.setAttribute("aria-label", ariaLabel);
  g.setAttribute("aria-disabled", isLocked ? "true" : "false");

  const fill = document.createElementNS(SVG_NS, "circle");
  fill.setAttribute("cx", String(pos.x));
  fill.setAttribute("cy", String(pos.y));
  fill.setAttribute("r", String(NODE_RADIUS));
  fill.setAttribute("class", "fill");
  g.appendChild(fill);

  const ring = document.createElementNS(SVG_NS, "circle");
  ring.setAttribute("cx", String(pos.x));
  ring.setAttribute("cy", String(pos.y));
  ring.setAttribute("r", String(NODE_RADIUS));
  ring.setAttribute("class", "ring");
  g.appendChild(ring);

  if (state.isStarMilestone) {
    const star = document.createElementNS(SVG_NS, "polygon");
    star.setAttribute("points", STAR_POINTS);
    star.setAttribute("transform", `translate(${pos.x} ${pos.y})`);
    star.setAttribute("class", "star");
    g.appendChild(star);
  } else {
    const text = document.createElementNS(SVG_NS, "text");
    text.setAttribute("x", String(pos.x));
    text.setAttribute("y", String(pos.y));
    text.textContent = String(displayNumber);
    g.appendChild(text);
  }

  // Transparent hit area (always last so it's on top).
  const hit = document.createElementNS(SVG_NS, "circle");
  hit.setAttribute("cx", String(pos.x));
  hit.setAttribute("cy", String(pos.y));
  hit.setAttribute("r", String(HIT_RADIUS));
  hit.setAttribute("class", "hit");
  g.appendChild(hit);

  return g;
}
